import { useContext, useEffect, useState } from "react"
import { useRecoilState } from "recoil"
import { SDKContext } from "src/context/SDKContext"
import axios from "src/shared/axios"
import {
  journeyUiSelector,
  topicsAndSubtopics,
  useJourneyPageTransition
} from "../../../recoil/atoms/journeyUI"
import { topicSelector } from "../../../recoil/atoms/lessons"
import CircleTransition from "../../journey/circleTransition"
import { Progress } from "../../journey/tile"
import TopicJourney from "../../journey/topicJourney"
import { useCustomToast } from "../../../hooks/useToast"
import { useHistory } from "react-router-dom"
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "src/components/common/popover"
import { MenuIconBtn } from "src/components/common/menuIconBtn"
import { BiSolidLogOut } from "react-icons/bi"
import { FaMoneyBill } from "react-icons/fa"
import { BsPersonVideo3 } from "react-icons/bs"
import { MdOutlineMenu } from "react-icons/md"
import { Routes } from "src/constants"
import HamburgerNavMenu from "src/components/common/HamburgerNavMenu"

export type Topic = {
  id: number
  name: string
  progress: {
    start: Progress
    new: Progress
  }
  subtopics: Topic[]
}

function wait(time) {
  return new Promise(res => setTimeout(res, time))
}

async function retry<T>(func: () => T, errorHandler: () => void, retries = 3) {
  if (retries === 0) {
    console.error("Requests failed")
    errorHandler()
    return
  }
  try {
    await func()
  } catch (err) {
    if (err.response.status === 401) return

    await wait(50)
    retry(func, errorHandler, --retries)
  }
}

function Journey() {
  const meetingClient = useContext(SDKContext)

  const [activeTopic, setActiveTopic] = useRecoilState(topicSelector)
  const [journeyUi, setJourneyUi] = useRecoilState(journeyUiSelector)

  const [topics, setTopicsAndSubtopics] = useRecoilState(topicsAndSubtopics)

  const [personalRoomHash, setPersonalRoomHash] = useState<string>(null)
  const history = useHistory()

  const logout = async () => {
    const res = await axios.post(
      meetingClient?.envConfig.USER_LOGOUT,
      {},
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json"
        }
      }
    )
    if (res.status == 200) {
      history.replace(Routes.LOGIN)
    }
  }

  const showToast = useCustomToast({
    toastOptions: {
      containerStyle: {
        margin: "0.1rem"
      }
    },
    titleStyles: {
      fontWeight: 700
    }
  })

  useJourneyPageTransition()

  useEffect(() => {
    if (!meetingClient?.envConfig.JOURNEY_URL) return

    retry(
      () =>
        axios
          .get(meetingClient?.envConfig.JOURNEY_URL, {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json"
            }
          })
          .then(res =>
            res.data.data.map(d => ({
              ...d,
              progress: {
                start: 0,
                new: 0
              },
              subtopics: d.subtopics.map(s => ({
                ...s,
                progress: { start: 0, new: 0 }
              }))
            }))
          )
          .then(setTopicsAndSubtopics),
      () => showToast("Error, please try again")
    )

    retry(
      () =>
        axios
          .get(meetingClient?.envConfig.PERSONAL_ROOM_URL, {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json"
            }
          })
          .then(res => res.data.data.moderatorHash)
          .then(setPersonalRoomHash),
      () => showToast("Error, please try again")
    )
  }, [meetingClient?.envConfig.JOURNEY_URL])

  const enableAnimation = journeyUi.transition === "topicsToSubtopics"
  return (
    <>
      <TopicJourney
        topics={topics ?? []}
        drillDown={topic => {
          setActiveTopic(topic)
          setJourneyUi({ transition: "topicsToSubtopics", id: topic.id })
        }}
        menuItemsDeselected={!activeTopic}
      />
      {activeTopic && (
        <CircleTransition
          position={{
            x: activeTopic.centerPos.x,
            y: activeTopic.centerPos.y
          }}
          color={activeTopic.color}
        >
          {animationState => {
            if (animationState !== "FINISHED" && enableAnimation) return null

            return (
              <TopicJourney.SubTopics
                topics={
                  topics.find(topic => topic.id === activeTopic.id).subtopics
                }
                topicId={activeTopic.id}
                background={activeTopic.color}
                title={activeTopic.name}
                onClose={() => {
                  setJourneyUi({ transition: "subtopicsToTopics" })
                }}
                personalRoomHash={personalRoomHash}
              />
            )
          }}
        </CircleTransition>
      )}
      <HamburgerNavMenu
        items={["account", "avatar", "plans", "logout"]}
        color="white"
        transparant
      />
    </>
  )
}

export default Journey
